import * as yup from "yup";
import { FormWrapper } from "./styles";
import { FC, useRef, useState, useEffect } from "react";
import { BasicForm, Block } from "../../../styles/basicStyles";
import { useTranslation } from "next-i18next";
import { useValidationSchema } from "../../../hooks/validations/useValidationSchema";
import { PostContactRequest } from "../../../api/requests/startProject";
import { StartProjectContactInfo, StartProjectForm } from "../../../models/startProject";
import { withTypes } from "react-final-form";
import { useRouter } from "next/router";
import { colors } from "../../../styles/colors";
import { showError } from "../../../hooks/notifications/showNotification";
import { isEmail, isUrl } from "../../../hooks/validations/genericValidations";
import ReCAPTCHA from 'react-google-recaptcha';
import FormFields from "./formFields";
import Button from "../../../components/button";
import dayjs from "dayjs";
import Header from "./header";

const { Form } = withTypes<StartProjectForm>();

interface ProjectFormProps {
  contactInfo?: StartProjectContactInfo;
}

const ProjectForm: FC<ProjectFormProps> = ({ contactInfo }) => {
  const { t, i18n: { language } } = useTranslation();
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const [isMobile, _setMobile] = useState<boolean>(false);
  const recaptchaRef = useRef<any>(null);

  const isMobileRef = useRef(isMobile);
  const setMobile = (data: boolean) => {
    isMobileRef.current = data;
    _setMobile(data);
  };

  useEffect(() => {
    handleResize();
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleResize = () => {
    if(window.innerWidth < 768) {
      if(!isMobileRef.current) setMobile(true);
    } 
    else {
      if(isMobileRef.current) setMobile(false);
    }
  };

  const onSubmit = async (values: StartProjectForm) => {
    try {
      setLoading(true);

      const recaptchaValue = recaptchaRef?.current?.getValue();
      if(!recaptchaValue) {
        setLoading(false);

        return showError({
          title: t('ERROR'),
          message: t('MUST_CHECK_RECAPTCHA')
        })
      }

      const { success, message } = await PostContactRequest({
        ...values,
        date: dayjs(),
        language,
        recaptchaValue
      });

      if(success) {
        // Push data to Google Tag Manager
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "contact_form_submitted"
        });

        return router.push("/success");
      }
      else {
        showError({
          title: t('ERROR'),
          message: message
        })
      }

      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  return (
    <FormWrapper
      pt={{ md: 3.875, xxs: 3.5 }}
      pb={{ md: 10.75, xxs: 4.438 }}
    >
      <Header contactInfo={contactInfo} />
      <Block w='100%' maxW={{ md: '70%', xxs: '100%' }}>
        <Form
          onSubmit={onSubmit}
          validate={useValidationSchema(
            yup.object({
              name: yup.string().required().test('at-least-3-chars', 'name_too_short', (value) => {
                if(!value) return false;
                return value.length >= 3;
              }),
              email: isEmail.required(),
              contact: yup.string().notRequired(),
              interests: yup.array().of(yup.string()).required(),
              budget: yup.string().required(),
              website: isUrl.notRequired(),
              about: yup.string().required(),
              privacy: yup.boolean().required(),
              referral: yup.array().of(yup.string()).required(),
              other_referral: yup.string().when('referral', ([referral], schema) => {
                if(Array.isArray(referral) && referral.includes('5')) return schema.required();
                return schema.notRequired();
              })
            })
          )}
          initialValues={{}}
          render={({ handleSubmit, form: { reset } }) => {
            return (
              <BasicForm
                onSubmit={async (event: any) => {
                  const result = await handleSubmit(event);
                  if(result) reset();
                }}
              >
                <FormFields />
                {
                  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY &&
                  <ReCAPTCHA
                    key={isMobile ? 'mobile' : 'desktop'} // This property is to force the re-render when isMobile changes
                    ref={recaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                    size={isMobile ? 'compact' : 'normal'}
                  />
                }
                <Block mt={2}>
                  <Button
                    text={t("SUBMIT")}
                    type="submit"
                    variant="primary"
                    loading={loading}
                    textColor={colors.white}
                    bgColor='#5747B2'
                  />
                </Block>
              </BasicForm>
            );
          }}
        />
      </Block>
    </FormWrapper>
  );
};

export default ProjectForm;
